import type { ShortcutAction } from '../input/shortcuts';
import {
  handleShortcutActions,
  isMacPlatform,
  isTextInputTarget,
} from '../input/shortcuts';
import {
  createActionButton,
  createDivider,
  createGroup,
  createToggleButton,
  setToggleButtonState,
} from './toolbarParts';

export abstract class ToolbarBase {
  protected panMode = false;
  protected gridEnabled = false;
  protected snapEnabled = false;
  protected invertEnabled = false;
  protected gridAutoEnabledBySnap = false;
  protected zoomDisplay: HTMLSpanElement | null = null;
  protected undoBtn: HTMLButtonElement | null = null;
  protected redoBtn: HTMLButtonElement | null = null;
  protected keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  protected gridBtn: HTMLButtonElement | null = null;
  protected snapBtn: HTMLButtonElement | null = null;
  protected invertBtn: HTMLButtonElement | null = null;
  protected snapActiveClass = 'snap-active';
  protected actionButtonOptions: { className?: string; preventPenHover?: boolean } = {};

  protected abstract onPanModeChange(): void;

  setPanMode(enabled: boolean): void {
    this.panMode = enabled;
    this.onPanModeChange();
  }

  getPanMode(): boolean {
    return this.panMode;
  }

  setGridEnabled(enabled: boolean): void {
    this.gridEnabled = enabled;
    setToggleButtonState(this.gridBtn, this.gridEnabled);
  }

  setSnapEnabled(enabled: boolean): void {
    this.snapEnabled = enabled;
    setToggleButtonState(this.snapBtn, this.snapEnabled, this.snapActiveClass);
  }

  setInvertEnabled(enabled: boolean): void {
    this.invertEnabled = enabled;
    setToggleButtonState(this.invertBtn, this.invertEnabled);
  }

  protected createGroup(label: string): HTMLDivElement {
    return createGroup(label);
  }

  protected createToolbarRow(className: string): HTMLDivElement {
    const row = document.createElement('div');
    row.className = className;
    return row;
  }

  protected createSidebarSection(extraClass?: string): HTMLDivElement {
    const section = document.createElement('div');
    section.className = extraClass ? `sidebar-section ${extraClass}` : 'sidebar-section';
    return section;
  }

  protected createSidebarLabel(text: string): HTMLDivElement {
    const label = document.createElement('div');
    label.className = 'sidebar-label';
    label.textContent = text;
    return label;
  }

  protected createSidebarToolSection(options: {
    tools: Array<{ tool: string; icon: string; title: string; onClick: () => void }>;
    pan?: { icon: string; title: string; onClick: () => void };
    className?: string;
  }): HTMLDivElement {
    const section = this.createSidebarSection(options.className);

    for (const tool of options.tools) {
      section.appendChild(
        this.createSidebarToolButton(tool.tool, tool.icon, tool.title, tool.onClick)
      );
    }

    if (options.pan) {
      section.appendChild(
        this.createSidebarToolButton('pan', options.pan.icon, options.pan.title, options.pan.onClick)
      );
    }

    return section;
  }

  protected appendSidebarToolButtons(options: {
    container: HTMLElement;
    buttons: Array<{
      key: string;
      tool: string;
      icon: string;
      title: string;
      onClick: () => void;
      className?: string;
      active?: boolean;
    }>;
  }): Record<string, HTMLButtonElement> {
    const refs: Record<string, HTMLButtonElement> = {};

    for (const button of options.buttons) {
      const btn = this.createSidebarToolButton(button.tool, button.icon, button.title, button.onClick);
      const buttonOptions: { className?: string } = {};
      if (button.className) {
        buttonOptions.className = button.className;
      }
      this.applyButtonOptions(btn, buttonOptions);
      if (button.active) {
        btn.classList.add('active');
      }
      options.container.appendChild(btn);
      refs[button.key] = btn;
    }

    return refs;
  }

  protected appendToggleButtons(options: {
    container: HTMLElement;
    buttons: Array<{
      key: string;
      icon: string;
      title: string;
      onClick: () => void;
    }>;
  }): Record<string, HTMLButtonElement> {
    const refs: Record<string, HTMLButtonElement> = {};

    for (const button of options.buttons) {
      const btn = this.createToggleButton(button.icon, button.title, button.onClick);
      options.container.appendChild(btn);
      refs[button.key] = btn;
    }

    return refs;
  }

  protected createActionGroup(): HTMLDivElement {
    const group = this.createGroup('');
    group.classList.add('action-group');
    return group;
  }

  protected buildActionGroup(options: {
    actions: Array<{
      key: string;
      icon: string;
      title: string;
      onClick: () => void;
      kind?: 'clear' | 'action';
      className?: string;
      testId?: string;
      disabled?: boolean;
    }>;
  }): { group: HTMLDivElement; buttons: Record<string, HTMLButtonElement> } {
    const group = this.createActionGroup();
    const buttons: Record<string, HTMLButtonElement> = {};

    for (const action of options.actions) {
        const button =
          action.kind === 'clear'
            ? this.createClearButton(action.icon, action.title, action.onClick)
            : this.createActionButton(action.icon, action.title, action.onClick);

        const actionOptions: { className?: string; testId?: string; disabled?: boolean } = {};
        if (action.className) {
          actionOptions.className = action.className;
        }
        if (action.testId) {
          actionOptions.testId = action.testId;
        }
        if (action.disabled !== undefined) {
          actionOptions.disabled = action.disabled;
        }
        this.applyButtonOptions(button, actionOptions);

        buttons[action.key] = button;
        group.appendChild(button);
      }

    return { group, buttons };
  }

  protected createExportGroup(options: {
    icon: string;
    title: string;
    onClick?: () => void;
  }): HTMLDivElement {
    const group = this.createGroup('');
    group.classList.add('export-group');
    group.appendChild(
      this.createActionButton(options.icon, options.title, (): void => options.onClick?.())
    );
    return group;
  }

  protected createClearButton(icon: string, title: string, onClick: () => void): HTMLButtonElement {
    const button = this.createActionButton(icon, title, onClick);
    button.classList.add('clear-btn');
    return button;
  }

  protected createDivider(className?: string): HTMLDivElement {
    return createDivider(className);
  }

  protected applyButtonOptions(
    button: HTMLButtonElement,
    options: { className?: string; testId?: string; disabled?: boolean }
  ): void {
    if (options.className) {
      options.className
        .split(' ')
        .filter(Boolean)
        .forEach((className) => {
          button.classList.add(className);
        });
    }
    if (options.testId) {
      button.dataset['testid'] = options.testId;
    }
    if (options.disabled !== undefined) {
      button.disabled = options.disabled;
    }
  }

  protected createActionButton(
    icon: string,
    title: string,
    onClick: () => void,
    options: { className?: string; preventPenHover?: boolean } = {}
  ): HTMLButtonElement {
    const className = options.className ?? this.actionButtonOptions.className;
    const preventPenHover = options.preventPenHover ?? this.actionButtonOptions.preventPenHover;

    const setActionIcon = (btn: HTMLButtonElement, iconValue: string): void =>
      this.setButtonIcon(btn, iconValue);
    const actionOptions: {
      icon: string;
      title: string;
      onClick: () => void;
      setIcon: (btn: HTMLButtonElement, icon: string) => void;
      className?: string;
      preventPenHover?: boolean;
    } = {
      icon,
      title,
      onClick,
      setIcon: setActionIcon,
    };

    if (className !== undefined) {
      actionOptions.className = className;
    }
    if (preventPenHover !== undefined) {
      actionOptions.preventPenHover = preventPenHover;
    }

    return createActionButton(actionOptions);
  }

  protected createToggleButton(
    icon: string,
    title: string,
    onClick: () => void,
    options: { className?: string } = {}
  ): HTMLButtonElement {
    const className = options.className;

    const setToggleIcon = (btn: HTMLButtonElement, iconValue: string): void =>
      this.setButtonIcon(btn, iconValue);
    const toggleOptions: {
      icon: string;
      title: string;
      onClick: () => void;
      setIcon: (btn: HTMLButtonElement, icon: string) => void;
      className?: string;
    } = {
      icon,
      title,
      onClick,
      setIcon: setToggleIcon,
    };

    if (className !== undefined) {
      toggleOptions.className = className;
    }

    return createToggleButton(toggleOptions);
  }

  protected createSidebarToolButton(
    tool: string,
    icon: string,
    title: string,
    onClick: () => void
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'sidebar-tool-btn';
    btn.dataset['tool'] = tool;
    this.setButtonIcon(btn, icon);
    btn.title = title;
    btn.addEventListener('click', onClick);
    return btn;
  }

  protected createSidebarContainer(className: string, id: string): HTMLDivElement {
    const sidebar = document.createElement('div');
    sidebar.className = className;
    sidebar.id = id;
    return sidebar;
  }

  protected mountBeforeCanvas(element: HTMLElement): void {
    const canvasContainer = document.getElementById('canvas-container');
    canvasContainer?.parentElement?.insertBefore(element, canvasContainer);
  }

  protected mountAfterCanvas(element: HTMLElement): void {
    const canvasContainer = document.getElementById('canvas-container');
    canvasContainer?.parentElement?.appendChild(element);
  }

  protected createZoomDisplay(
    onReset: () => void,
    options: { testId?: string } = {}
  ): HTMLSpanElement {
    const zoomDisplay = document.createElement('span');
    zoomDisplay.className = 'zoom-display';
    zoomDisplay.textContent = '100%';
    zoomDisplay.title = 'Click to reset (Cmd 0)';
    if (options.testId) {
      zoomDisplay.dataset['testid'] = options.testId;
    }
    zoomDisplay.addEventListener('click', (): void => {
      onReset();
    });
    return zoomDisplay;
  }

  protected updateSidebarToolButtons(sidebarEl: HTMLElement | null, currentTool: string): void {
    const buttons = sidebarEl?.querySelectorAll('.sidebar-tool-btn');
    buttons?.forEach((btn): void => {
      const toolBtn = btn as HTMLButtonElement;
      const tool = toolBtn.dataset['tool'];
      if (tool === 'pan') {
        toolBtn.classList.toggle('active', this.panMode);
      } else {
        toolBtn.classList.toggle('active', tool === currentTool && !this.panMode);
      }
    });
  }

  protected togglePanMode(
    sidebarEl: HTMLElement | null,
    currentTool: string,
    onChange?: (enabled: boolean) => void
  ): void {
    this.panMode = !this.panMode;
    this.updateSidebarToolButtons(sidebarEl, currentTool);
    onChange?.(this.panMode);
  }

  protected updateUndoRedoButtons(
    undoBtn: HTMLButtonElement | null,
    redoBtn: HTMLButtonElement | null,
    canUndo: boolean,
    canRedo: boolean
  ): void {
    if (undoBtn) {
      undoBtn.disabled = !canUndo;
    }
    if (redoBtn) {
      redoBtn.disabled = !canRedo;
    }
  }

  protected updateZoomLabel(zoomDisplay: HTMLSpanElement | null, scale: number): void {
    if (zoomDisplay) {
      zoomDisplay.textContent = `${Math.round(scale * 100)}%`;
    }
  }

  updateZoom(scale: number): void {
    this.updateZoomLabel(this.zoomDisplay, scale);
  }

  updateHistoryButtons(canUndo: boolean, canRedo: boolean): void {
    this.updateUndoRedoButtons(this.undoBtn, this.redoBtn, canUndo, canRedo);
  }

  protected createHistoryGroup(options: {
    undo: { icon: string; title: string; onClick?: () => void; disabled?: boolean };
    redo: { icon: string; title: string; onClick?: () => void; disabled?: boolean };
  }): { group: HTMLDivElement; undoBtn: HTMLButtonElement; redoBtn: HTMLButtonElement } {
    const historyGroup = this.createGroup('');
    historyGroup.classList.add('history-group');

    const undoBtn = this.createActionButton(options.undo.icon, options.undo.title, (): void =>
      options.undo.onClick?.()
    );
    const undoOptions: { disabled?: boolean } = {};
    if (options.undo.disabled !== undefined) {
      undoOptions.disabled = options.undo.disabled;
    }
    this.applyButtonOptions(undoBtn, undoOptions);
    historyGroup.appendChild(undoBtn);

    const redoBtn = this.createActionButton(options.redo.icon, options.redo.title, (): void =>
      options.redo.onClick?.()
    );
    const redoOptions: { disabled?: boolean } = {};
    if (options.redo.disabled !== undefined) {
      redoOptions.disabled = options.redo.disabled;
    }
    this.applyButtonOptions(redoBtn, redoOptions);
    historyGroup.appendChild(redoBtn);

    return { group: historyGroup, undoBtn, redoBtn };
  }

  protected createViewGroup(options: {
    zoomOut: { icon: string; title: string; onClick?: () => void };
    zoomIn: { icon: string; title: string; onClick?: () => void };
    reset: { onClick?: () => void; testId?: string };
    fitToScreen?: { icon: string; title: string; onClick?: () => void };
    grid: { icon: string; title: string; onToggle?: (enabled: boolean) => void; testId?: string };
    snap: { icon: string; title: string; onToggle?: (enabled: boolean) => void; testId?: string };
    invert: { icon: string; title: string; onToggle?: (enabled: boolean) => void; testId?: string };
    help: { icon: string; title: string; onClick?: () => void };
  }): { group: HTMLDivElement; zoomDisplay: HTMLSpanElement } {
    const zoomGroup = this.createGroup('View');
    zoomGroup.classList.add('view-group');

    zoomGroup.appendChild(
      this.createActionButton(options.zoomOut.icon, options.zoomOut.title, (): void =>
        options.zoomOut.onClick?.()
      )
    );

    const zoomDisplay = this.createZoomDisplay(
      (): void => options.reset.onClick?.(),
      options.reset.testId ? { testId: options.reset.testId } : {}
    );
    this.zoomDisplay = zoomDisplay;
    zoomGroup.appendChild(zoomDisplay);

    zoomGroup.appendChild(
      this.createActionButton(options.zoomIn.icon, options.zoomIn.title, (): void =>
        options.zoomIn.onClick?.()
      )
    );

    if (options.fitToScreen) {
      zoomGroup.appendChild(
        this.createActionButton(
          options.fitToScreen.icon,
          options.fitToScreen.title,
          (): void => options.fitToScreen?.onClick?.()
        )
      );
    }

    this.gridBtn = this.createActionButton(options.grid.icon, options.grid.title, (): void => {
      const next = !this.gridEnabled;
      // If manually disabling grid, clear the auto-enabled flag
      if (!next) {
        this.gridAutoEnabledBySnap = false;
      }
      this.setGridEnabled(next);
      options.grid.onToggle?.(next);
    });
    const gridOptions: { testId?: string } = {};
    if (options.grid.testId) {
      gridOptions.testId = options.grid.testId;
    }
    this.applyButtonOptions(this.gridBtn, gridOptions);
    zoomGroup.appendChild(this.gridBtn);
    this.setGridEnabled(this.gridEnabled);

    this.snapBtn = this.createActionButton(options.snap.icon, options.snap.title, (): void => {
      const next = !this.snapEnabled;

      // If enabling snap and grid is off, auto-enable grid
      if (next && !this.gridEnabled) {
        this.gridAutoEnabledBySnap = true;
        this.setGridEnabled(true);
        options.grid.onToggle?.(true);
      }

      // If disabling snap and grid was auto-enabled, disable grid too
      if (!next && this.gridAutoEnabledBySnap) {
        this.gridAutoEnabledBySnap = false;
        this.setGridEnabled(false);
        options.grid.onToggle?.(false);
      }

      this.setSnapEnabled(next);
      options.snap.onToggle?.(next);
    });
    const snapOptions: { testId?: string } = {};
    if (options.snap.testId) {
      snapOptions.testId = options.snap.testId;
    }
    this.applyButtonOptions(this.snapBtn, snapOptions);
    zoomGroup.appendChild(this.snapBtn);
    this.setSnapEnabled(this.snapEnabled);

    this.invertBtn = this.createActionButton(options.invert.icon, options.invert.title, (): void => {
      const next = !this.invertEnabled;
      this.setInvertEnabled(next);
      options.invert.onToggle?.(next);
    });
    const invertOptions: { testId?: string } = {};
    if (options.invert.testId) {
      invertOptions.testId = options.invert.testId;
    }
    this.applyButtonOptions(this.invertBtn, invertOptions);
    zoomGroup.appendChild(this.invertBtn);
    this.setInvertEnabled(this.invertEnabled);

    zoomGroup.appendChild(
      this.createActionButton(options.help.icon, options.help.title, (): void =>
        options.help.onClick?.()
      )
    );

    return { group: zoomGroup, zoomDisplay };
  }

  protected buildTopBarGroups(options: {
    history: {
      undo: { icon: string; title: string; onClick?: () => void; disabled?: boolean };
      redo: { icon: string; title: string; onClick?: () => void; disabled?: boolean };
    };
    export: { icon: string; title: string; onClick?: () => void };
    view: {
      zoomOut: { icon: string; title: string; onClick?: () => void };
      zoomIn: { icon: string; title: string; onClick?: () => void };
      reset: { onClick?: () => void; testId?: string };
      fitToScreen?: { icon: string; title: string; onClick?: () => void };
      grid: { icon: string; title: string; onToggle?: (enabled: boolean) => void; testId?: string };
      snap: { icon: string; title: string; onToggle?: (enabled: boolean) => void; testId?: string };
      invert: { icon: string; title: string; onToggle?: (enabled: boolean) => void; testId?: string };
      help: { icon: string; title: string; onClick?: () => void };
    };
  }): { historyGroup: HTMLDivElement; exportGroup: HTMLDivElement; viewGroup: HTMLDivElement } {
    const historyControls = this.createHistoryGroup(options.history);
    this.undoBtn = historyControls.undoBtn;
    this.redoBtn = historyControls.redoBtn;

    const exportGroup = this.createExportGroup(options.export);
    const viewControls = this.createViewGroup(options.view);

    return {
      historyGroup: historyControls.group,
      exportGroup,
      viewGroup: viewControls.group,
    };
  }

  protected setKeydownHandler(handler: (e: KeyboardEvent) => void): void {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }
    this.keydownHandler = handler;
    document.addEventListener('keydown', handler);
  }

  protected clearKeydownHandler(): void {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
  }

  protected setShortcutHandler(
    actions: ShortcutAction[],
    options: { allowTextInput?: boolean } = {}
  ): void {
    this.setKeydownHandler((e): void => {
      if (!options.allowTextInput && isTextInputTarget(e.target)) {
        return;
      }
      const isMac = isMacPlatform();
      handleShortcutActions(e, actions, isMac);
    });
  }

  protected createIconElement(svgMarkup: string): SVGSVGElement {
    const fragment = document.createRange().createContextualFragment(svgMarkup);
    const element = fragment.firstElementChild;
    if (!element || element.tagName.toLowerCase() !== 'svg') {
      return document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    }
    return element as SVGSVGElement;
  }

  protected setButtonIcon(button: HTMLButtonElement, svgMarkup: string): void {
    button.replaceChildren(this.createIconElement(svgMarkup));
  }
}
